import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { api } from '../../api.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { AccountTree } from '../../components/bookkeeping/AccountTree.js';
import type { Account, AccountNode } from '../../types/bookkeeping.js';

const ACCOUNT_TYPES = ['asset', 'liability', 'equity', 'revenue', 'expense'] as const;

const AccountCreateSchema = z.object({
  code: z.string().min(1).max(50),
  name: z.string().min(1).max(200),
  type: z.enum(ACCOUNT_TYPES),
  parent_id: z.string().uuid().nullable().optional(),
  currency: z.string().length(3),
});
type AccountCreateForm = z.infer<typeof AccountCreateSchema>;

/** Flatten a nested AccountNode tree into a flat array for dropdowns. */
function flattenTree(nodes: AccountNode[]): Account[] {
  const result: Account[] = [];
  function walk(ns: AccountNode[]) {
    for (const n of ns) {
      result.push(n);
      if (n.children.length > 0) walk(n.children);
    }
  }
  walk(nodes);
  return result;
}

export function Accounts() {
  const { t } = useTranslation('bookkeeping');
  const { activeOrgId } = useAuth();
  const qc = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['bookkeeping-accounts', activeOrgId],
    queryFn: () => api<{ tree: AccountNode[] }>('/bookkeeping/accounts'),
    enabled: !!activeOrgId,
  });

  // The API already returns the tree structure; flatten for the parent dropdown.
  const tree = data?.tree ?? [];
  const allAccounts = flattenTree(tree);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<AccountCreateForm>({
    resolver: zodResolver(AccountCreateSchema),
    defaultValues: { currency: 'USD', type: 'asset', parent_id: null },
  });

  const createAccount = useMutation({
    mutationFn: (input: AccountCreateForm) =>
      api('/bookkeeping/accounts', {
        method: 'POST',
        body: JSON.stringify({
          ...input,
          parent_id: input.parent_id || null,
        }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['bookkeeping-accounts'] });
      setModalOpen(false);
      reset();
    },
  });

  const onSubmit = handleSubmit((data) => createAccount.mutate(data));

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-xl font-semibold">{t('nav.accounts')}</h1>
        <button
          type="button"
          onClick={() => setModalOpen(true)}
          className="rounded bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700"
        >
          {t('accounts.addButton')}
        </button>
      </div>

      {isLoading ? (
        <p className="text-sm text-slate-500">{t('common:loading')}</p>
      ) : (
        <AccountTree nodes={tree} />
      )}

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h2 className="mb-4 text-lg font-semibold">{t('accounts.addButton')}</h2>
            <form onSubmit={onSubmit} className="space-y-4">
              <label className="block">
                <span className="text-sm">{t('accounts.fields.code')}</span>
                <input
                  {...register('code')}
                  className="mt-1 w-full rounded border px-3 py-2 text-sm"
                />
                {errors.code && <span className="text-xs text-red-600">{errors.code.message}</span>}
              </label>

              <label className="block">
                <span className="text-sm">{t('accounts.fields.name')}</span>
                <input
                  {...register('name')}
                  className="mt-1 w-full rounded border px-3 py-2 text-sm"
                />
                {errors.name && <span className="text-xs text-red-600">{errors.name.message}</span>}
              </label>

              <label className="block">
                <span className="text-sm">{t('accounts.fields.type')}</span>
                <select
                  {...register('type')}
                  className="mt-1 w-full rounded border px-3 py-2 text-sm"
                >
                  {ACCOUNT_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {t(`accounts.types.${type}`)}
                    </option>
                  ))}
                </select>
                {errors.type && <span className="text-xs text-red-600">{errors.type.message}</span>}
              </label>

              <label className="block">
                <span className="text-sm">{t('accounts.fields.parent')}</span>
                <select
                  {...register('parent_id')}
                  className="mt-1 w-full rounded border px-3 py-2 text-sm"
                >
                  <option value="">—</option>
                  {allAccounts.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.code} — {a.name}
                    </option>
                  ))}
                </select>
              </label>

              <label className="block">
                <span className="text-sm">{t('accounts.fields.currency')}</span>
                <input
                  {...register('currency')}
                  maxLength={3}
                  className="mt-1 w-full rounded border px-3 py-2 text-sm uppercase"
                />
                {errors.currency && (
                  <span className="text-xs text-red-600">{errors.currency.message}</span>
                )}
              </label>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setModalOpen(false);
                    reset();
                  }}
                  className="rounded border px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || createAccount.isPending}
                  className="rounded bg-blue-600 px-4 py-2 text-sm text-white disabled:opacity-50"
                >
                  {t('accounts.addButton')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
